import { Alert, Slide, type SlideProps, Snackbar } from '@mui/material'
import React, { useEffect, useLayoutEffect, useState } from 'react'

import { toastContainerElement, type ToastOptions } from 'lib/toast'

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />
}

export function ToastContainer() {
  const [toastQueue, setToastQueue] = useState<ToastOptions[]>([])
  const [activeToast, setActiveToast] = useState<ToastOptions | null>(null)

  // listen for toast events and queue them
  useLayoutEffect(() => {
    const listener = ({ detail: toast }: CustomEvent) => {
      setToastQueue((q) => [...q, toast])
    }
    toastContainerElement.addEventListener('toast:add', listener)

    return () => {
      toastContainerElement.removeEventListener('toast:add', listener)
    }
  }, [setToastQueue, setActiveToast, activeToast])

  // activate the next toast in the queue (if there is one) when the active one is cleared
  useEffect(() => {
    if (!activeToast && toastQueue.length > 0) {
      const [next, ...queue] = toastQueue
      setActiveToast(next ?? null)
      setToastQueue(queue)
    }
  }, [activeToast, toastQueue])

  // conditionally render the entire Snackbar and not just the child Alert
  // because otherwise the transitions do not behave properly
  if (!activeToast) {
    return null
  }

  return (
    <Snackbar
      data-test-snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      autoHideDuration={activeToast.time ?? 5_000}
      id="ToastContainerSnackbar"
      open={Boolean(activeToast)}
      TransitionComponent={SlideTransition}
      onClose={(_e, reason) => {
        // do not close when the user clicks elsewhere
        if (reason === 'clickaway') {
          return
        }

        setActiveToast(null)
      }}
    >
      {/* MUI Snackbar only supports one child Alert component so we only ever render the first one, and we queue the rest */}
      <Alert
        action={
          /*
           * If `action` is provided, render it (regardless if `closeOnClick` is true or false).
           * If `closeOnClick` is true, render a dummy span to make the toast clickable to close it.
           * If `closeOnClick` is falsy, we pass in null so the default close button will be rendered
           */
          activeToast.action ?? (activeToast.closeOnClick ? <span /> : null)
        }
        className={`gremlin-toast toast-${activeToast.type}`}
        data-test-toast={activeToast.testKey}
        severity={activeToast.type}
        onClick={
          activeToast.closeOnClick ? () => setActiveToast(null) : undefined
        }
        onClose={() => {
          // user manually clicks the close button
          setActiveToast(null)
        }}
      >
        {activeToast.text}
      </Alert>
    </Snackbar>
  )
}
